// ============================================================================
// PAYMENT VOUCHER — form + view modals (ADR-0039 / ADR-0041).
//
// The voucher opens empty and asks one question: what are you paying? Sections
// appear as they are used, so a simple supplier payment never shows an empty
// accounts grid, and mixing the two is a click rather than a mode change.
//
// The cash is never typed. Each section contributes to
//
//     Net Payable = what adds − what reduces
//
// which is exactly what the bank is credited with. Create-only: a posted
// payment is cancelled and re-entered, never edited.
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { usePaymentModeOptions } from 'hooks/usePaymentModeOptions';
import { ConfirmDialog } from 'components/modals';
import OutstandingDisplay from 'components/common/OutstandingDisplay';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import {
  paymentService, coaLedgerService, partyService, serviceService,
  recoverableExpenseService, entityAccountingConfigService, configService,
} from 'api/services';
import { PAYMENT_LINE_TYPES, PAY_AS, isPaymentPartyLine } from 'api/types';
import type {
  PaymentDto, CreatePaymentRequest, CreatePaymentLineRequest, RecoveryTargetDto, DeductionReasonDto,
} from 'api/types';
import type { StatementLinePrefill } from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

/** Encoded "charge to" value: `${entityId}::${businessUnitId}` (mirrors receipts). */
const TARGET_SEP = '::';

const INSTRUMENT_OPTIONS: SelectOption[] = [
  { value: 'TRANSFER', label: 'Bank transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
];

const PAY_AS_OPTIONS: SelectOption[] = [
  { value: PAY_AS.SUPPLIER_ADVANCE, label: 'Supplier advance' },
  { value: PAY_AS.CUSTOMER_REFUND, label: 'Customer refund' },
];

const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => { const n = parseFloat(String(s).replace(/,/g, '')); return isNaN(n) ? 0 : n; };
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const r2 = (n: number) => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------
// STATE
// ---------------------------------------------------------------------------

interface BillRow {
  openItemId: string;
  referenceNo: string;
  dueDate: string;
  /** The bill's own (transaction) currency — what it was raised in (ADR-0031). */
  currencyCode: string;
  /** Outstanding in the bill's own currency. */
  fcOutstanding: number;
  /** Outstanding in base, at the bill's booked rate. */
  baseOutstanding: number;
  /** "Paying" — editable; ALWAYS in the PAYMENT's currency: the money leaving the bank. */
  allocated: string;
  /** "Settling" — editable; the BILL's own currency being discharged. Used only when the bill's
   *  currency differs from the payment's (ADR-0031 revision); empty otherwise, where the amount
   *  paid IS the amount settled. */
  settling: string;
  /**
   * "Withheld" (ADR-0060) — in the BILL's currency: what we hold back (damage, shortage, rate
   * difference…). The bill closes for it and it stays open as a withheld item. Editing it moves
   * Paying the other way, so the bill stays closed.
   */
  withheld: string;
  reason: string;
  remarks: string;
  /** When the item itself is a withheld amount left by an earlier payment — what it is. */
  deductionNote?: string;
}

interface SupplierSection {
  key: number;
  partyId: string;
  partyLabel: string;
  payAmount: string;
  bills: BillRow[];
  loadingBills: boolean;
  /** Where an unallocated remainder lands — stated, never inferred (ADR-0041 §4). */
  payAs: string;
}

interface GlRow {
  key: number;
  ledgerId: string;
  ledgerLabel: string;
  chargeTo: string;
  amount: string;
  isCredit: boolean | null;   // null = take the direction from the ledger
  narration: string;
}

interface RecRow {
  key: number;
  partyId: string;
  partyLabel: string;
  serviceId: string;
  serviceLabel: string;
  chargeTo: string;
  amount: string;
  narration: string;
}

interface PaymentForm {
  paymentDate: string;
  paidFromLedgerId: string;
  paidFromLedgerLabel: string;
  /** The account's currency, which is the payment's. Not chosen separately — see loadCashBank. */
  currencyCode: string;
  /** That currency's rate to the entity's own. 1 when they are the same. */
  exchangeRate: string;
  instrumentType: string;
  instrumentNumber: string;
  instrumentDate: string;
  draweeBank: string;
  narration: string;
}

const initialForm = (): PaymentForm => ({
  paymentDate: today(),
  paidFromLedgerId: '', paidFromLedgerLabel: '', currencyCode: '', exchangeRate: '1',
  instrumentType: 'TRANSFER', instrumentNumber: '', instrumentDate: '', draweeBank: '',
  narration: '',
});

export interface PaymentVoucherFormModalProps {
  isOpen: boolean;
  onPosted: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  onRestoreModal?: (instanceId?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
  /** Posting from a bank statement line (ADR-0055 §5): account, date and amount come from the line. */
  fromStatementLine?: StatementLinePrefill;
  /** Replaces the plain create — the statement posts the voucher and matches it to the line. */
  onSubmitOverride?: (payload: CreatePaymentRequest) => Promise<unknown>;
}

export const PaymentVoucherFormModal: React.FC<PaymentVoucherFormModalProps> = ({
  isOpen, onPosted, onClose, tabId, moduleName, createInstanceId, onRestoreModal, minimizeRef, fromStatementLine, onSubmitOverride,
}) => {
  const { minimize } = useModalMinimize();
  const { selectedEntity, selectedBusinessUnit } = useAuthContext();
  const sessionBuId = selectedBusinessUnit?.id;
  const fy = useSelectedFinancialYear();

  const keySeq = useRef(0);
  const nextKey = () => ++keySeq.current;

  const [form, setForm] = useState<PaymentForm>(initialForm);

  // Payment modes come from the company-wide master (ADR-0055 §6); the static list is the fallback.

  const modeOptions = usePaymentModeOptions(INSTRUMENT_OPTIONS, form.instrumentType);
  const [suppliers, setSuppliers] = useState<SupplierSection[]>([]);
  const [glRows, setGlRows] = useState<GlRow[]>([]);
  const [recRows, setRecRows] = useState<RecRow[]>([]);

  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [targets, setTargets] = useState<RecoveryTargetDto[]>([]);
  const [recoverableEnabled, setRecoverableEnabled] = useState(false);
  const [quickAdds, setQuickAdds] = useState<{ id: string; label: string }[]>([]);
  const [reasonOptions, setReasonOptions] = useState<SelectOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const supRef = useRef(suppliers); supRef.current = suppliers;
  const glRef = useRef(glRows); glRef.current = glRows;
  const recRef = useRef(recRows); recRef.current = recRows;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const ledgerLabelCache = useRef(new Map<string, string>());
  const ledgerCurrencyCache = useRef(new Map<string, string>());
  const partyLabelCache = useRef(new Map<string, string>());
  const serviceLabelCache = useRef(new Map<string, string>());

  /** Dirty snapshot — user-editable values only, so cached labels are not edits. */
  const snapshot = useCallback((f: PaymentForm, sup: SupplierSection[], gl: GlRow[], rec: RecRow[]) =>
    JSON.stringify({
      f: { ...f, paidFromLedgerLabel: '' },
      sup: sup.map(x => ({ p: x.partyId, a: x.payAmount, pa: x.payAs,
        b: x.bills.map(b => [b.openItemId, b.allocated, b.settling, b.withheld, b.reason, b.remarks]) })),
      gl: gl.map(x => ({ l: x.ledgerId, c: x.chargeTo, a: x.amount, cr: x.isCredit, n: x.narration })),
      rec: rec.map(x => ({ p: x.partyId, s: x.serviceId, c: x.chargeTo, a: x.amount, n: x.narration })),
    }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    recoverableExpenseService.getRecoveryTargets().then(setTargets).catch(() => setTargets([]));
    paymentService.getDeductionReasons()
      .then((rs: DeductionReasonDto[]) => setReasonOptions(rs.map(x => ({ value: x.value, label: x.label }))))
      .catch(() => setReasonOptions([]));
    entityAccountingConfigService.getConfig()
      .then(c => setFunctionalCurrency(c?.functionalCurrencyCode || ''))
      .catch(() => setFunctionalCurrency(''));
    configService.resolve('FEATURE.RECOVERABLE_EXPENSE')
      .then(r => setRecoverableEnabled(String(r?.value).toLowerCase() === 'true'))
      .catch(() => setRecoverableEnabled(false));
    coaLedgerService.getAll({ pageSize: 25, ledgerKind: 'GL', flagCode: 'PAY_QUICK' })
      .then(r => setQuickAdds(r.data.map(l => ({ id: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }))))
      .catch(() => setQuickAdds([]));

    if (isRestoreRef.current) { isRestoreRef.current = false; setErrors({}); return; }
    const f = fromStatementLine
      ? { ...initialForm(), paymentDate: fromStatementLine.date, paidFromLedgerId: fromStatementLine.bankLedgerId, paidFromLedgerLabel: fromStatementLine.bankLedgerLabel,
          instrumentNumber: (fromStatementLine.reference || '').slice(0, 50), narration: fromStatementLine.description || '' }
      : { ...initialForm(), paymentDate: defaultDateIn(fy) };
    if (fromStatementLine) {
      // The account's currency decides the voucher's (a foreign account then asks for the rate).
      Promise.all([coaLedgerService.getById(fromStatementLine.bankLedgerId), entityAccountingConfigService.getConfig()])
        .then(([l, cfg]) => {
          const ccy = l?.data?.currencyCode || '';
          const foreign = !!ccy && !!cfg?.functionalCurrencyCode && ccy !== cfg.functionalCurrencyCode;
          setForm(p => ({ ...p, currencyCode: ccy, exchangeRate: foreign ? '' : '1' }));
        })
        .catch(() => { /* home currency assumed */ });
    }
    setForm(f); setSuppliers([]); setGlRows([]); setRecRows([]);
    initialSnapshotRef.current = snapshot(f, [], [], []);
    setErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return snapshot(form, suppliers, glRows, recRows) !== initialSnapshotRef.current;
  }, [form, suppliers, glRows, recRows, snapshot]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  // --- Minimize: park a half-typed payment, sections and allocations included.
  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const idx = createInstanceIdRef.current || '1';
    const savedForm = { ...formRef.current };
    const savedSup = supRef.current.map(x => ({ ...x, bills: x.bills.map(b => ({ ...b })) }));
    const savedGl = glRef.current.map(x => ({ ...x }));
    const savedRec = recRef.current.map(x => ({ ...x }));
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? snapshot(savedForm, savedSup, savedGl, savedRec) !== savedSnapshot : false;
    const who = savedSup[0]?.partyLabel;
    const title = who ? `Payment - ${who} (${idx})` : `Payment (${idx})`;

    minimize({
      id: `${tabId}-form-modal-create-${idx}`,
      title, moduleName: moduleName || 'Payment Voucher', tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm); setSuppliers(savedSup); setGlRows(savedGl); setRecRows(savedRec);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(idx);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, snapshot]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const err = (f: string) => errors[f];
  const clearErr = useCallback((f: string) =>
    setErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; }), []);
  const updateForm = useCallback((patch: Partial<PaymentForm>) => setForm(p => ({ ...p, ...patch })), []);

  // --- Option loaders -------------------------------------------------------

  const loadCashBank = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await coaLedgerService.getAll({
      pageSize: 100, searchTerm: input || undefined, ledgerKind: 'GL',
      availableToBusinessUnitId: sessionBuId,
    });
    return res.data
      .filter(l => l.subtypeCode === 'BANK_ACCOUNT' || l.subtypeCode === 'CASH_ACCOUNT')
      .map(l => {
        const label = `${l.ledgerCode} - ${l.ledgerName}`;
        ledgerLabelCache.current.set(l.id, label);
        // The account's own currency decides the payment's — money cannot leave a pula
        // account in dollars. Remembered here so choosing an account sets the currency
        // rather than the user setting it twice and risking a mismatch (ADR-0041 §9).
        ledgerCurrencyCache.current.set(l.id, l.currencyCode || '');
        return { value: l.id, label };
      });
  }, [sessionBuId]);

  /**
   * Nominal ledgers for the account lines. `chargeTo` scopes the list: a line charged to
   * another business unit must offer THAT unit's permitted ledgers. ledgerKind GL keeps
   * party subledgers out — a party balance is reached through its party (ADR-0040/0041).
   */
  const loadLedgers = useCallback(async (input: string, chargeTo?: string): Promise<SelectOption[]> => {
    const buId = (chargeTo && chargeTo.split(TARGET_SEP)[1]) || sessionBuId;
    const res = await coaLedgerService.getAll({
      pageSize: 50, searchTerm: input || undefined, ledgerKind: 'GL',
      availableToBusinessUnitId: buId,
    });
    return res.data.map(l => {
      const label = `${l.ledgerCode} - ${l.ledgerName}`;
      ledgerLabelCache.current.set(l.id, label);
      return { value: l.id, label };
    });
  }, [sessionBuId]);

  /**
   * The party picker defaults to who we currently owe, largest first, and widens to the full
   * master once the user types. Role is shown, never used as a filter — that would block
   * refunds and hide anyone whose classification was never maintained (ADR-0041 §5).
   */
  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    const owed = await paymentService.getPayableParties(input || undefined, 25).catch(() => []);
    const opts: SelectOption[] = owed.map(o => {
      const what = o.hasCustomerCredit && !o.hasSupplierBills ? 'in credit' : 'owed';
      partyLabelCache.current.set(o.partyId, `${o.partyCode} — ${o.partyName}`);
      return { value: o.partyId, label: `${o.partyCode} — ${o.partyName}  ·  ${money(o.amountOwed)} ${what}` };
    });
    if (!input) return opts;
    const seen = new Set(opts.map(o => o.value));
    const all = await partyService.getAll({ pageSize: 25, searchTerm: input });
    for (const p of all.data) {
      if (seen.has(p.id)) continue;
      const label = `${p.partyCode} — ${p.partyName}`;
      partyLabelCache.current.set(p.id, label);
      opts.push({ value: p.id, label });
    }
    return opts;
  }, []);

  const loadCustomers = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined, partyType: 'CUSTOMER' });
    return res.data.map(p => {
      const label = `${p.partyCode} — ${p.partyName}`;
      partyLabelCache.current.set(p.id, label);
      return { value: p.id, label };
    });
  }, []);

  const loadReimbItems = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await serviceService.getAll({ pageSize: 50, searchTerm: input || undefined, itemType: 'REIMBURSEMENT' });
    return res.data.map(s => {
      const label = `${s.serviceCode} — ${s.serviceName}`;
      serviceLabelCache.current.set(s.id, label);
      return { value: s.id, label };
    });
  }, []);

  const chargeToOptions = useMemo(() => {
    const opts: SelectOption[] = [{ value: '', label: 'This business unit' }];
    const ordered = [...targets].sort((a, b) => {
      if (a.entityId === selectedEntity?.id) return -1;
      if (b.entityId === selectedEntity?.id) return 1;
      return (a.entityName || '').localeCompare(b.entityName || '');
    });
    for (const t of ordered) {
      const isOwn = t.entityId === selectedEntity?.id;
      for (const bu of t.businessUnits || []) {
        opts.push({
          value: `${t.entityId}${TARGET_SEP}${bu.businessUnitId}`,
          label: isOwn ? (bu.unitName || '') : `${t.entityName} — ${bu.unitName}`,
        });
      }
    }
    return opts;
  }, [targets, selectedEntity]);

  // --- Section management ---------------------------------------------------

  const addSupplier = useCallback(() => {
    setSuppliers(p => [...p, {
      key: nextKey(), partyId: '', partyLabel: '', payAmount: '', bills: [],
      loadingBills: false, payAs: PAY_AS.SUPPLIER_ADVANCE,
    }]);
  }, []);

  const addGlRow = useCallback((ledgerId = '', ledgerLabel = '') => {
    setGlRows(p => [...p, {
      key: nextKey(), ledgerId, ledgerLabel, chargeTo: '', amount: '', isCredit: null, narration: '',
    }]);
  }, []);

  const addRecRow = useCallback(() => {
    setRecRows(p => [...p, {
      key: nextKey(), partyId: '', partyLabel: '', serviceId: '', serviceLabel: '',
      chargeTo: '', amount: '', narration: '',
    }]);
  }, []);

  const updateSupplier = useCallback((key: number, patch: Partial<SupplierSection>) =>
    setSuppliers(p => p.map(s => (s.key === key ? { ...s, ...patch } : s))), []);
  const updateGl = useCallback((key: number, patch: Partial<GlRow>) =>
    setGlRows(p => p.map(g => (g.key === key ? { ...g, ...patch } : g))), []);
  const updateRec = useCallback((key: number, patch: Partial<RecRow>) =>
    setRecRows(p => p.map(r => (r.key === key ? { ...r, ...patch } : r))), []);

  /** Load what we owe this party — supplier bills and customer credit alike. */
  const pickParty = useCallback(async (key: number, partyId: string) => {
    clearErr(`sup-${key}`);
    updateSupplier(key, {
      partyId, partyLabel: partyLabelCache.current.get(partyId) || '',
      bills: [], loadingBills: !!partyId,
    });
    if (!partyId) return;
    try {
      const items = await paymentService.getOpenBills(partyId);
      updateSupplier(key, {
        loadingBills: false,
        bills: items.map(i => ({
          openItemId: String(i.id),
          referenceNo: i.referenceNo,
          dueDate: (i.dueDate || i.itemDate || '').slice(0, 10),
          currencyCode: i.currencyCode || functionalCurrency,
          fcOutstanding: i.fcBalance,
          baseOutstanding: i.baseBalance,
          allocated: '',
          settling: '',
          withheld: '',
          reason: '',
          remarks: '',
          deductionNote: i.deductionKind
            ? ['Withheld', i.sourceReferenceNo ? `on ${i.sourceReferenceNo}` : '', i.reasonLabel || i.reasonCode || '', i.remarks || ''].filter(Boolean).join(' · ')
            : undefined,
        })),
      });
    } catch {
      updateSupplier(key, { bills: [], loadingBills: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSupplier, clearErr, functionalCurrency]);

  // ---- Cross-currency settlement helpers -----------------------------------
  // Mirror of the server's PayableSettlement (ADR-0031 revision). "Paying" (`allocated`) is
  // ALWAYS in the PAYMENT's currency — the money leaving the bank. When the bill's currency
  // differs, "Settling" (`settling`) states how much of the BILL's currency it discharges —
  // the money alone cannot say, once the rate has moved — and the exchange difference is
  // paying in base minus settling at the bill's booked rate.
  const fnCcyRef = useRef(functionalCurrency); fnCcyRef.current = functionalCurrency;
  const payCcyOf = () => formRef.current.currencyCode || fnCcyRef.current;
  const payRateOf = () => { const r = num(formRef.current.exchangeRate); return r > 0 ? r : 1; };
  /** Bill raised in a different currency than the payment — the two-figure case. */
  const isCrossBill = (b: BillRow) => b.currencyCode !== payCcyOf();
  /** The bill's booked rate to base — invariant across part-settlements (base/fc). */
  const billRateOf = (b: BillRow) => (b.fcOutstanding > 0 ? b.baseOutstanding / b.fcOutstanding : 1);
  /** Payment-currency equivalent of the bill's outstanding AT ITS BOOKED RATE — the seed for
   *  "Paying" on a cross-currency bill (FX difference 0 until the user states the real figure). */
  const bookedPayEquiv = (b: BillRow) => r2(b.baseOutstanding / payRateOf());
  /** Bill-currency amount this row discharges: stated for a cross bill, = paying otherwise. */
  const billFcSettling = (b: BillRow) =>
    isCrossBill(b) ? Math.min(num(b.settling), b.fcOutstanding)
      : Math.min(num(b.allocated), b.fcOutstanding);
  /** Realized FX in base: cash paid − liability discharged at the booked rate; positive = LOSS
   *  (debits FX Gain / Loss), negative = gain (credits). Mirror of PayableSettlement.Apply. */
  const billFxDiff = (b: BillRow) => {
    const paying = num(b.allocated);
    const fc = billFcSettling(b);
    if (fc <= 0 || paying <= 0) return 0;
    return isCrossBill(b)
      ? r2(paying * payRateOf() - fc * billRateOf(b))
      : r2(fc * payRateOf() - fc * billRateOf(b));
  };

  /** Spread the typed amount oldest-first. Only seeds the figures — every cell stays editable;
   *  a cross-currency bill is seeded at its booked rate, so its FX starts at 0 until the user
   *  states what the bank actually pays. */
  const autoAllocate = useCallback((key: number) => {
    setSuppliers(p => p.map(s => {
      if (s.key !== key) return s;
      let left = num(s.payAmount);
      return {
        ...s,
        bills: s.bills.map(b => {
          const cap = isCrossBill(b) ? bookedPayEquiv(b) : b.fcOutstanding;
          const a = Math.min(left, cap);
          left = r2(left - a);
          if (a <= 0) return { ...b, allocated: '', settling: '' };
          const settling = isCrossBill(b)
            ? (a >= cap ? b.fcOutstanding : Math.min(r2((a * payRateOf()) / billRateOf(b)), b.fcOutstanding))
            : 0;
          return {
            ...b,
            allocated: String(r2(a)),
            settling: isCrossBill(b) ? String(settling) : '',
          };
        }),
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ADR-0060: a withheld amount, in the bill's currency, capped at the bill's outstanding. */
  const billWithheld = (b: BillRow) => Math.max(0, Math.min(num(b.withheld), b.fcOutstanding));
  /** A bill without a withheld amount loses its reason and remarks too. */
  const withWithheld = (b: BillRow, w: number): BillRow =>
    w > 0.004 ? { ...b, withheld: String(r2(w)) } : { ...b, withheld: '', reason: '', remarks: '' };
  /** The payment-currency money for a bill-currency figure, at the bill's booked rate (cross-currency bills). */
  const payEquivOf = (b: BillRow, fc: number) => r2((fc * billRateOf(b)) / payRateOf());

  const setBillAmount = useCallback((
    key: number, openItemId: string, field: 'paying' | 'settling', value: string,
  ) => {
    setSuppliers(p => p.map(s => (s.key !== key ? s : {
      ...s,
      bills: s.bills.map(b => {
        if (b.openItemId !== openItemId) return b;
        const hadWithheld = billWithheld(b) > 0;
        if (field === 'settling') {
          // Bill-currency figure — hard-capped at what the bill has outstanding.
          const v = value === '' ? '' : String(Math.min(num(value), b.fcOutstanding));
          // A bill with a withheld amount stays closed: what is not settled by money is withheld.
          return hadWithheld ? withWithheld({ ...b, settling: v }, b.fcOutstanding - num(v)) : { ...b, settling: v };
        }
        // Paying: capped at the outstanding only when the currencies match (there the amount
        // paid IS the amount settled). Across currencies the cap is meaningless — paying more
        // base than the discharge was booked at is exactly how a loss looks.
        const capped = isCrossBill(b) ? value : (value === '' ? '' : String(Math.min(num(value), b.fcOutstanding)));
        if (hadWithheld && !isCrossBill(b)) return withWithheld({ ...b, allocated: capped }, b.fcOutstanding - num(capped));
        return { ...b, allocated: capped };
      }),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Editing Withheld moves Paying the other way (ADR-0060): Paying = Outstanding − Withheld, so the
   * bill stays closed. On a bill in another currency, Settling takes the rest and Paying follows at
   * the booked rate. Withheld in full leaves nothing paid — allowed.
   */
  const setWithheld = useCallback((key: number, openItemId: string, value: string) => {
    setSuppliers(p => p.map(s => (s.key !== key ? s : {
      ...s,
      bills: s.bills.map(b => {
        if (b.openItemId !== openItemId) return b;
        const w = value === '' ? 0 : Math.min(Math.max(0, num(value)), b.fcOutstanding);
        const rest = r2(b.fcOutstanding - w);
        const next = withWithheld(b, w);
        if (isCrossBill(b))
          return { ...next, settling: rest > 0 ? String(rest) : '', allocated: rest > 0 ? String(payEquivOf(b, rest)) : '' };
        return { ...next, allocated: rest > 0 ? String(rest) : '' };
      }),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setWithheldField = useCallback((key: number, openItemId: string, patch: Partial<Pick<BillRow, 'reason' | 'remarks'>>) => {
    setSuppliers(p => p.map(s => (s.key !== key ? s : { ...s, bills: s.bills.map(b => (b.openItemId === openItemId ? { ...b, ...patch } : b)) })));
  }, []);

  /**
   * "Close ticked bills in full" (ADR-0060): the amount being paid fills the ticked bills oldest
   * first; whatever each ticked bill does not get becomes its withheld amount, so every ticked bill
   * closes. Reasons already picked stay.
   */
  const closeTickedInFull = useCallback((key: number) => {
    setSuppliers(p => p.map(s => {
      if (s.key !== key) return s;
      let left = num(s.payAmount);
      return {
        ...s,
        bills: s.bills.map(b => {
          const ticked = num(b.allocated) > 0 || num(b.settling) > 0 || billWithheld(b) > 0;
          if (!ticked) return b;
          if (isCrossBill(b)) {
            const cap = bookedPayEquiv(b);
            const a = Math.max(0, Math.min(left, cap));
            left = r2(left - a);
            const settling = a <= 0 ? 0 : a >= cap ? b.fcOutstanding : Math.min(r2((a * payRateOf()) / billRateOf(b)), b.fcOutstanding);
            return withWithheld({ ...b, allocated: a > 0 ? String(r2(a)) : '', settling: settling > 0 ? String(settling) : '' }, b.fcOutstanding - settling);
          }
          const a = Math.max(0, Math.min(left, b.fcOutstanding));
          left = r2(left - a);
          return withWithheld({ ...b, allocated: a > 0 ? String(r2(a)) : '', settling: '' }, b.fcOutstanding - a);
        }),
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBill = useCallback((key: number, openItemId: string, on: boolean) => {
    setSuppliers(p => p.map(s => (s.key !== key ? s : {
      ...s,
      bills: s.bills.map(b => {
        if (b.openItemId !== openItemId) return b;
        if (!on) return { ...b, allocated: '', settling: '', withheld: '', reason: '', remarks: '' };
        return isCrossBill(b)
          ? { ...b, settling: String(b.fcOutstanding), allocated: String(bookedPayEquiv(b)) }
          : { ...b, allocated: String(b.fcOutstanding), settling: '' };
      }),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching the paid-from account can change the payment's currency, which changes what every
  // typed figure MEANS — so clear the allocations rather than reinterpret them silently.
  const prevPayCcy = useRef<string>('');
  useEffect(() => {
    const ccy = form.currencyCode || functionalCurrency;
    if (prevPayCcy.current && ccy && prevPayCcy.current !== ccy) {
      setSuppliers(p => p.map(s => ({
        ...s,
        bills: s.bills.map(b => (b.allocated || b.settling || b.withheld ? { ...b, allocated: '', settling: '', withheld: '', reason: '', remarks: '' } : b)),
      })));
    }
    prevPayCcy.current = ccy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.currencyCode, functionalCurrency]);

  // --- Derived totals -------------------------------------------------------

  const supTotals = useMemo(() => suppliers.map(s => {
    const allocated = r2(s.bills.reduce((t, b) => t + num(b.allocated), 0));
    const asked = num(s.payAmount);
    // An empty amount box means "pay exactly what I ticked".
    const total = asked > 0 ? asked : allocated;
    // ADR-0060: withheld amounts kept open, in base at each bill's booked rate.
    const withheldBase = r2(s.bills.reduce((t, b) => t + billWithheld(b) * billRateOf(b), 0));
    const withheldCount = s.bills.filter(b => billWithheld(b) > 0).length;
    return { key: s.key, allocated, asked, total, remainder: r2(Math.max(0, total - allocated)), withheldBase, withheldCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [suppliers]);

  const totals = useMemo(() => {
    const supTotal = supTotals.reduce((t, s) => t + s.total, 0);
    const recTotal = recRows.reduce((t, r) => t + num(r.amount), 0);
    const glAdds = glRows.filter(g => g.isCredit !== true).reduce((t, g) => t + num(g.amount), 0);
    const glReduces = glRows.filter(g => g.isCredit === true).reduce((t, g) => t + num(g.amount), 0);
    const gross = r2(supTotal + recTotal + glAdds);
    const reduces = r2(glReduces);
    return { gross, reduces, net: r2(gross - reduces) };
  }, [supTotals, recRows, glRows]);

  const anySection = suppliers.length > 0 || glRows.length > 0 || recRows.length > 0;

  // Foreign only when the account's currency and the entity's are both known and differ. An
  // unknown currency is treated as home rather than prompting for a rate against nothing.
  const isForeignPayment = !!form.currencyCode && !!functionalCurrency
    && form.currencyCode !== functionalCurrency;

  // Amounts on this screen are entered in the ACCOUNT's currency, so that is what the totals
  // are in. Labelling them with the entity's currency would name the wrong money.
  const payCurrency = form.currencyCode || functionalCurrency;

  const crossEntityTargets = useMemo(() => {
    const set = new Set<string>();
    for (const c of [...glRows.map(g => g.chargeTo), ...recRows.map(r => r.chargeTo)]) {
      if (!c) continue;
      const [entId] = c.split(TARGET_SEP);
      if (entId && entId !== selectedEntity?.id) {
        set.add(targets.find(t => t.entityId === entId)?.entityName || 'another company');
      }
    }
    return [...set];
  }, [glRows, recRows, targets, selectedEntity]);

  // --- Save -----------------------------------------------------------------

  const validate = useCallback((): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.paymentDate) e.paymentDate = 'Payment date is required';
    if (!form.paidFromLedgerId) e.paidFromLedgerId = 'Choose the cash or bank account the money leaves';
    if (isForeignPayment && !(Number(form.exchangeRate) > 0)) {
      e.exchangeRate = `Enter today's rate for ${form.currencyCode} — bills clear at the rate they were booked at, and the difference is posted as an exchange gain or loss`;
    }
    if (!anySection) e.sections = 'Add a supplier or an account to say what you are paying';

    suppliers.forEach(s => {
      const t = supTotals.find(x => x.key === s.key)!;
      if (!s.partyId) { e[`sup-${s.key}`] = 'Pick who is being paid'; return; }
      if (t.total <= 0 && t.withheldCount === 0) { e[`sup-${s.key}`] = 'Enter an amount, or tick the bills being settled'; return; }
      const noReason = s.bills.find(b => billWithheld(b) > 0 && !b.reason);
      if (noReason) { e[`sup-${s.key}`] = `${noReason.referenceNo} has a withheld amount — pick a reason`; return; }
      const over = s.bills.find(b => (isCrossBill(b) ? num(b.settling) : num(b.allocated)) + billWithheld(b) > b.fcOutstanding + 0.004);
      if (over) { e[`sup-${s.key}`] = `${over.referenceNo}: paying and withheld together are more than its ${over.currencyCode} ${money(over.fcOutstanding)} outstanding`; return; }
      if (t.allocated > t.total + 0.004) {
        e[`sup-${s.key}`] = `Allocated ${payCurrency} ${money(t.allocated)} is more than the ${payCurrency} ${money(t.total)} being paid — a bill's remaining balance stays outstanding`;
        return;
      }
      // A cross-currency settlement needs BOTH figures: what is paid and how much of the bill's
      // own currency it discharges — the exchange difference is exactly the gap between them.
      for (const b of s.bills) {
        if (b.currencyCode === payCurrency) continue;
        const paying = num(b.allocated);
        const settling = num(b.settling);
        if (paying > 0 && settling <= 0) {
          e[`sup-${s.key}`] = `${b.referenceNo} is in ${b.currencyCode}: say how much ${b.currencyCode} the ${payCurrency} ${money(paying)} settles, so the exchange difference can be posted`;
          return;
        }
        if (settling > 0 && paying <= 0) {
          e[`sup-${s.key}`] = `${b.referenceNo}: settling ${b.currencyCode} ${money(settling)} needs the ${payCurrency} amount being paid for it`;
          return;
        }
      }
    });

    glRows.forEach(g => {
      if (!g.ledgerId) e[`gl-${g.key}`] = 'Pick the account to post to';
      else if (num(g.amount) <= 0) e[`gl-${g.key}`] = 'Enter an amount';
    });

    recRows.forEach(r => {
      if (!r.partyId) e[`rec-${r.key}`] = 'Pick the customer this is recoverable from';
      else if (!r.serviceId) e[`rec-${r.key}`] = 'Pick the reimbursement item';
      else if (num(r.amount) <= 0) e[`rec-${r.key}`] = 'Enter an amount';
    });

    if (!e.sections && totals.gross <= 0) {
      e.sections = 'Nothing is being paid — add what the money is for.';
    } else if (!e.sections && totals.net <= 0) {
      e.sections = 'The amounts reducing this payment cancel out what is being paid, so nothing would leave the bank.';
    }
    if (fromStatementLine && !e.sections && Math.abs(totals.net - fromStatementLine.amount) > 0.004) {
      e.sections = `The bank debited ${money(fromStatementLine.amount)} — this payment nets ${money(totals.net)}. They must be equal.`;
    }
    return e;
  }, [form, suppliers, supTotals, glRows, recRows, anySection, totals, payCurrency, fromStatementLine]);

  const handlePostClick = useCallback(() => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(e)[0]}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    setErrors({});
    setSaveConfirmOpen(true);
  }, [validate]);

  const buildLines = useCallback((): CreatePaymentLineRequest[] => {
    const out: CreatePaymentLineRequest[] = [];

    suppliers.forEach(s => {
      const t = supTotals.find(x => x.key === s.key)!;
      // Each allocated bill becomes its own settlement line. The engine takes the ledger from
      // the open item, so a customer credit and a supplier bill need no distinction here.
      s.bills.forEach(b => {
        const a = num(b.allocated);
        const w = billWithheld(b);
        // A bill with a withheld amount posts even when nothing is paid for it (ADR-0060): it
        // closes, and the whole amount stays open as a withheld item.
        if (a > 0 || w > 0) {
          const cross = b.currencyCode !== payCurrency;
          out.push({
            lineType: PAYMENT_LINE_TYPES.BILL_SETTLEMENT,
            partyId: s.partyId, settledOpenItemId: b.openItemId, amount: a,
            // Cross-currency: the bill-currency figure being discharged — the server refuses a
            // cross settlement without it, and posts the difference to FX Gain / Loss.
            settledFcAmount: cross && a > 0 ? num(b.settling) : null,
            withheldAmount: w > 0 ? w : null,
            withheldReasonCode: w > 0 ? b.reason : null,
            withheldRemarks: w > 0 ? (b.remarks.trim() || null) : null,
          });
        }
      });
      // Whatever is left must say which relationship it settles (ADR-0041 §4).
      if (t.remainder > 0) {
        out.push({
          lineType: PAYMENT_LINE_TYPES.ON_ACCOUNT,
          partyId: s.partyId, payAs: s.payAs, amount: t.remainder,
        });
      }
    });

    recRows.forEach(r => {
      const [entId, buId] = r.chargeTo ? r.chargeTo.split(TARGET_SEP) : ['', ''];
      out.push({
        lineType: PAYMENT_LINE_TYPES.RECOVERABLE_EXPENSE,
        partyId: r.partyId, serviceId: r.serviceId,
        businessUnitId: buId || null,
        targetEntityId: entId && entId !== selectedEntity?.id ? entId : null,
        amount: num(r.amount), narration: r.narration.trim() || null,
      });
    });

    glRows.forEach(g => {
      const [entId, buId] = g.chargeTo ? g.chargeTo.split(TARGET_SEP) : ['', ''];
      out.push({
        lineType: PAYMENT_LINE_TYPES.GL_LINE,
        ledgerId: g.ledgerId,
        businessUnitId: buId || null,
        targetEntityId: entId && entId !== selectedEntity?.id ? entId : null,
        amount: num(g.amount),
        isCredit: g.isCredit,           // null → the ledger decides
        narration: g.narration.trim() || null,
      });
    });

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, supTotals, glRows, recRows, selectedEntity, payCurrency]);

  const handleConfirmPost = useCallback(async () => {
    setSaveConfirmOpen(false);
    setPosting(true);
    try {
      const payload: CreatePaymentRequest = {
        paymentDate: form.paymentDate,
        partyId: suppliers[0]?.partyId || null,
        paidFromLedgerId: form.paidFromLedgerId,
        // The account's currency, not a separately chosen one — the server derives the same
        // value from the account and refuses a mismatch, so this is display, not decision.
        currencyCode: form.currencyCode || functionalCurrency || null,
        exchangeRate: isForeignPayment ? Number(form.exchangeRate) : 1,
        instrumentType: form.instrumentType || null,
        instrumentNumber: form.instrumentNumber.trim() || null,
        instrumentDate: form.instrumentDate || null,
        draweeBank: form.draweeBank.trim() || null,
        narration: form.narration.trim() || null,
        lines: buildLines(),
      };
      if (onSubmitOverride) {
        // Posted and matched by the statement; the account and currency are the line's.
        await onSubmitOverride({ ...payload, currencyCode: null });
        setPosting(false);
        notificationService.toast.success('Posted and matched to the statement line.');
        onPosted(); onClose();
        return;
      }
      const saved = await paymentService.create(payload);
      setPosting(false);
      notificationService.toast.success(
        `Payment ${saved.paymentNumber || ''} posted — ${money(saved.netPaid)} paid.`);
      onPosted(); onClose();
    } catch (e: any) {
      setPosting(false);
      notificationService.toast.error(e?.response?.data?.message || 'Could not post the payment.', 7000);
    }
  }, [form, suppliers, functionalCurrency, buildLines, onPosted, onClose, onSubmitOverride]);

  if (!isOpen) return null;

  const labelStyle: React.CSSProperties = {
    fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block',
    color: 'var(--text-secondary)', fontWeight: 500,
  };
  const cellInput: React.CSSProperties = {
    width: '100%', textAlign: 'right', fontSize: '0.8125rem', padding: '0.3rem 0.45rem',
  };
  const sectHead: React.CSSProperties = {
    background: 'var(--bg-tertiary)', padding: '0.5rem 0.7rem', display: 'flex',
    flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem',
    borderBottom: '1px solid var(--border-color)',
  };
  const sectBox: React.CSSProperties = {
    border: '1px solid var(--border-color)', borderRadius: '0.5rem',
    overflow: 'hidden', marginTop: '1rem',
  };
  const isCash = form.instrumentType === 'CASH';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {posting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Posting…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{fromStatementLine ? 'Payment Voucher — from bank statement' : 'Payment Voucher'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={posting}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={posting}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {fromStatementLine && (
            <div style={{ fontSize: '0.8125rem', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.3)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem' }}>
              From the bank statement: {fromStatementLine.date} · <strong>{money(fromStatementLine.amount)}</strong>{fromStatementLine.description ? ` · ${fromStatementLine.description}` : ''}.
              The payment must net exactly this amount; it is matched to the line when posted.
            </div>
          )}
          {/* ---- Header ---- */}
          <div className="form-grid-4" style={{ alignItems: 'flex-end' }}>
            <div data-field="paymentDate">
              <label style={labelStyle}>Payment Date <span className="text-danger">*</span></label>
              <FyDateInput className={`form-input${err('paymentDate') ? ' form-field-error' : ''}`}
                value={form.paymentDate}
                onChange={v => { clearErr('paymentDate'); updateForm({ paymentDate: v }); }}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              {err('paymentDate') && <div className="form-error-message">{err('paymentDate')}</div>}
            </div>
            <div data-field="paidFromLedgerId">
              <SyutSelect label={<>Paid From <span className="text-danger">*</span></>} isAsync defaultOptions cacheOptions={false} isDisabled={!!fromStatementLine}
                loadOptions={loadCashBank} menuPortalTarget={document.body}
                seedOptions={form.paidFromLedgerId ? [{ value: form.paidFromLedgerId, label: form.paidFromLedgerLabel }] : undefined}
                value={form.paidFromLedgerId}
                onChange={v => {
                  const id = (v as string) || '';
                  clearErr('paidFromLedgerId'); clearErr('exchangeRate');
                  // Choosing the account chooses the currency. Reset the rate to 1 whenever the
                  // account is the entity's own currency, so a rate left over from a previous
                  // foreign account cannot silently restate a home-currency payment.
                  const ccy = ledgerCurrencyCache.current.get(id) || '';
                  const foreign = !!ccy && !!functionalCurrency && ccy !== functionalCurrency;
                  updateForm({
                    paidFromLedgerId: id,
                    paidFromLedgerLabel: ledgerLabelCache.current.get(id) || '',
                    currencyCode: ccy,
                    exchangeRate: foreign ? '' : '1',
                  });
                }}
                placeholder="Cash / bank account" error={err('paidFromLedgerId')} isClearable={false} size="sm" />
            </div>
            {isForeignPayment && (
              <div data-field="exchangeRate">
                <label style={labelStyle}>
                  Rate: 1 {form.currencyCode} = ? {functionalCurrency} <span className="text-danger">*</span>
                </label>
                <input type="number" step="0.000001" min="0"
                  className={`form-input${err('exchangeRate') ? ' form-field-error' : ''}`}
                  value={form.exchangeRate}
                  onChange={e => { clearErr('exchangeRate'); updateForm({ exchangeRate: e.target.value }); }}
                  placeholder="Today's rate"
                  title="Bills clear at the rate they were booked at; the difference against this rate is posted as an exchange gain or loss."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                {err('exchangeRate') && <div className="form-error-message">{err('exchangeRate')}</div>}
              </div>
            )}
            <div>
              <SyutSelect label="Paid By" options={modeOptions} value={form.instrumentType}
                onChange={v => updateForm({ instrumentType: (v as string) || 'TRANSFER' })}
                menuPortalTarget={document.body} isClearable={false} size="sm" />
            </div>
            <div>
              <label style={labelStyle}>{isCash ? 'Narration' : 'Reference No'}</label>
              <input type="text" className="form-input"
                value={isCash ? form.narration : form.instrumentNumber}
                onChange={e => updateForm(isCash ? { narration: e.target.value } : { instrumentNumber: e.target.value })}
                placeholder={isCash ? 'What this payment is for' : 'e.g. transfer ref'}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
          </div>

          {!isCash && (
            <div className="form-grid-3" style={{ marginTop: '0.7rem', alignItems: 'flex-end' }}>
              <div>
                <label style={labelStyle}>Instrument Date</label>
                <input type="date" className="form-input" value={form.instrumentDate}
                  onChange={e => updateForm({ instrumentDate: e.target.value })}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div>
                <label style={labelStyle}>Bank</label>
                <input type="text" className="form-input" value={form.draweeBank}
                  onChange={e => updateForm({ draweeBank: e.target.value })}
                  placeholder="Drawee / remitting bank"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div>
                <label style={labelStyle}>Narration</label>
                <input type="text" className="form-input" value={form.narration}
                  onChange={e => updateForm({ narration: e.target.value })}
                  placeholder="What this payment is for"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
          )}

          {/* ---- Empty state: one question, two answers ---- */}
          {!anySection && (
            <div data-field="sections" style={{
              border: '1px dashed var(--form-border-color)', borderRadius: '0.5rem',
              padding: '1.5rem 1rem', textAlign: 'center', background: 'var(--bg-secondary)',
              marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem', alignItems: 'center',
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>What are you paying?</strong>
                &nbsp;Pick one to start — you can add the other later.
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={addSupplier}
                  style={{ textAlign: 'left', padding: '0.7rem 1rem', minWidth: 220 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>A supplier or party</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Settle bills, pay an advance, refund a credit
                  </div>
                </button>
                <button className="btn btn-secondary" onClick={() => addGlRow()}
                  style={{ textAlign: 'left', padding: '0.7rem 1rem', minWidth: 220 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>An account directly</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Expense, asset, deposit or statutory payment
                  </div>
                </button>
              </div>
              {err('sections') && <div className="form-error-message">{err('sections')}</div>}
            </div>
          )}

          {/* ---- Supplier sections ---- */}
          {suppliers.map(s => {
            const t = supTotals.find(x => x.key === s.key)!;
            const sErr = err(`sup-${s.key}`);
            return (
              <div key={s.key} style={sectBox} data-field={`sup-${s.key}`}>
                <div style={sectHead}>
                  <div style={{ flex: '1 1 260px', minWidth: 220 }}>
                    <SyutSelect isAsync defaultOptions cacheOptions={false}
                      loadOptions={loadParties} menuPortalTarget={document.body}
                      seedOptions={s.partyId ? [{ value: s.partyId, label: s.partyLabel }] : undefined}
                      value={s.partyId}
                      onChange={v => pickParty(s.key, (v as string) || '')}
                      placeholder="Who are you paying?" isClearable size="sm" />
                  </div>
                  {s.partyId && (
                    <span className="badge">
                      {s.loadingBills ? 'Loading…'
                        : s.bills.length ? `${s.bills.length} outstanding` : 'nothing outstanding'}
                    </span>
                  )}
                  {/* Shared component (ADR-0042 §6): the same figure, under the same scope rules,
                      as every other screen showing this party's position. */}
                  <OutstandingDisplay partyId={s.partyId} side="payable" variant="inline" />
                  <span style={{ flex: '1 1 auto' }} />
                  <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                    onClick={() => setSuppliers(p => p.filter(x => x.key !== s.key))}>Remove</button>
                </div>

                <div style={{ padding: '0.7rem' }}>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                    <div style={{ width: 170 }}>
                      <label style={labelStyle}>Amount to pay</label>
                      <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                        value={s.payAmount} placeholder="0.00"
                        onChange={e => { clearErr(`sup-${s.key}`); updateSupplier(s.key, { payAmount: e.target.value }); }} />
                    </div>
                    <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }}
                      disabled={!s.bills.length} onClick={() => autoAllocate(s.key)}>
                      Allocate oldest first
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }}
                      disabled={!s.bills.length || num(s.payAmount) <= 0}
                      title="Tick the bills you are settling, enter the amount you are paying, then click: the money fills the ticked bills oldest first and whatever each one does not get becomes its withheld amount — every ticked bill closes"
                      onClick={() => closeTickedInFull(s.key)}>
                      Close ticked bills in full
                    </button>
                    <span style={{ flex: '1 1 auto' }} />
                    {t.remainder > 0 && (
                      <div style={{ width: 210 }}>
                        <SyutSelect label="Unallocated goes to" options={PAY_AS_OPTIONS} value={s.payAs}
                          onChange={v => updateSupplier(s.key, { payAs: (v as string) || PAY_AS.SUPPLIER_ADVANCE })}
                          menuPortalTarget={document.body} isClearable={false} size="sm" />
                      </div>
                    )}
                  </div>

                  {s.bills.length > 0 && (() => {
                    // Currency columns appear only when money actually crosses a currency: a
                    // foreign payment, or a bill raised in a currency other than the payment's.
                    const showCcy = isForeignPayment || s.bills.some(b => b.currencyCode !== payCurrency);
                    return (
                    <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                          <th style={{ width: 32, padding: '0.4rem' }} />
                          <th style={{ textAlign: 'left', padding: '0.4rem' }}>Reference</th>
                          <th style={{ width: 110, textAlign: 'left', padding: '0.4rem' }}>Due</th>
                          <th style={{ width: showCcy ? 150 : 130, textAlign: 'right', padding: '0.4rem' }}>
                            Outstanding{showCcy ? ' (bill ccy)' : ''}
                          </th>
                          {showCcy && (
                            <th style={{ width: 140, textAlign: 'right', padding: '0.4rem' }}>
                              Settling (bill ccy)
                            </th>
                          )}
                          <th style={{ width: 130, textAlign: 'right', padding: '0.4rem' }}>
                            Paying{showCcy ? ` (${payCurrency})` : ''}
                          </th>
                          <th style={{ width: 120, textAlign: 'right', padding: '0.4rem' }}
                            title="What you hold back from this bill. The bill closes for it, and it stays open on the supplier's ledger as a withheld item to settle later.">
                            Withheld{showCcy ? ' (bill ccy)' : ''}
                          </th>
                          <th style={{ width: 150, textAlign: 'left', padding: '0.4rem' }}>Reason</th>
                          <th style={{ width: showCcy ? 140 : 120, textAlign: 'right', padding: '0.4rem' }}>Still open</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.bills.map(b => {
                          const alloc = num(b.allocated);
                          const cross = b.currencyCode !== payCurrency;
                          const fcSettling = billFcSettling(b);
                          const withheldFc = billWithheld(b);
                          const fcOpen = r2(b.fcOutstanding - fcSettling - withheldFc);
                          const fx = billFxDiff(b);
                          const impliedRate = cross && fcSettling > 0 && alloc > 0
                            ? (alloc * (num(form.exchangeRate) > 0 ? num(form.exchangeRate) : 1)) / fcSettling
                            : null;
                          return (
                            <React.Fragment key={b.openItemId}>
                            <tr style={{ borderBottom: withheldFc > 0 ? 'none' : '1px solid var(--border-color)' }}>
                              <td style={{ textAlign: 'center' }}>
                                <input type="checkbox" checked={alloc > 0 || fcSettling > 0 || withheldFc > 0}
                                  aria-label={`Pay ${b.referenceNo}`}
                                  onChange={e => toggleBill(s.key, b.openItemId, e.target.checked)} />
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem' }}>
                                {b.referenceNo}
                                {b.deductionNote && (
                                  <div style={{ fontSize: '0.7rem', color: 'var(--warning-text, #92400e)' }}>{b.deductionNote}</div>
                                )}
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem' }}>{b.dueDate || '—'}</td>
                              <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {showCcy && <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{b.currencyCode}</span>}
                                {money(b.fcOutstanding)}
                              </td>
                              {showCcy && (
                                <td style={{ padding: '0.25rem 0.4rem' }}>
                                  {cross ? (
                                    // The bill's own currency being discharged — the second figure a
                                    // cross-currency settlement needs (the money alone cannot say).
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{b.currencyCode}</span>
                                      <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                                        value={b.settling} placeholder="—"
                                        onChange={e => { clearErr(`sup-${s.key}`); setBillAmount(s.key, b.openItemId, 'settling', e.target.value); }} />
                                    </div>
                                  ) : (
                                    <div style={{ textAlign: 'right', color: 'var(--text-muted)' }}
                                      title="Same currency as the payment — the amount paid is the amount settled">
                                      {alloc > 0 ? money(Math.min(alloc, b.fcOutstanding)) : '—'}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                                  value={b.allocated} placeholder="—"
                                  onChange={e => { clearErr(`sup-${s.key}`); setBillAmount(s.key, b.openItemId, 'paying', e.target.value); }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                                  value={b.withheld} placeholder="—" aria-label={`Withheld on ${b.referenceNo}`}
                                  onChange={e => { clearErr(`sup-${s.key}`); setWithheld(s.key, b.openItemId, e.target.value); }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                {withheldFc > 0 ? (
                                  <SyutSelect options={reasonOptions} value={b.reason}
                                    onChange={v => { clearErr(`sup-${s.key}`); setWithheldField(s.key, b.openItemId, { reason: (v as string) || '' }); }}
                                    menuPortalTarget={document.body} placeholder="Pick a reason" isClearable={false} size="sm" />
                                ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: fcOpen > 0.004 ? undefined : 'var(--text-muted)' }}>
                                {fcOpen > 0.004
                                  ? <>{showCcy && <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{b.currencyCode}</span>}{money(fcOpen)}</>
                                  : 'closed'}
                                {withheldFc > 0 && fcOpen <= 0.004 && (
                                  <div style={{ fontSize: '0.7rem', color: 'var(--warning-text, #92400e)' }}>{b.currencyCode} {money(withheldFc)} withheld stays open</div>
                                )}
                                {Math.abs(fx) >= 0.005 && (
                                  <div style={{ fontSize: '0.7rem', color: fx > 0 ? 'var(--danger-text, #b91c1c)' : 'var(--success-text, #15803d)' }}
                                    title={impliedRate != null
                                      ? `Implied rate 1 ${b.currencyCode} = ${impliedRate.toFixed(4)} ${functionalCurrency}; booked ${billRateOf(b).toFixed(4)}`
                                      : undefined}>
                                    FX {fx > 0 ? 'loss' : 'gain'} {money(Math.abs(fx))} {functionalCurrency}
                                  </div>
                                )}
                              </td>
                            </tr>
                            {withheldFc > 0 && (
                              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--warning-bg, #fef3c7)' }}>
                                <td />
                                <td colSpan={showCcy ? 8 : 7} style={{ padding: '0.25rem 0.4rem 0.4rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Withheld remarks</span>
                                    <input type="text" className="form-input" maxLength={500}
                                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.45rem' }}
                                      value={b.remarks} placeholder="e.g. GRN, damage report, what was agreed with the supplier"
                                      aria-label={`Remarks for the withheld amount on ${b.referenceNo}`}
                                      onChange={e => setWithheldField(s.key, b.openItemId, { remarks: e.target.value })} />
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    );
                  })()}

                  {s.partyId && !s.loadingBills && !s.bills.length && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      Nothing outstanding for this party — the whole amount will be recorded as
                      {' '}{s.payAs === PAY_AS.CUSTOMER_REFUND ? 'a customer refund' : 'a supplier advance'}.
                    </div>
                  )}

                  <div style={{
                    display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap',
                    marginTop: '0.55rem', fontSize: '0.8rem',
                  }}>
                    <span style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span className="badge" style={t.remainder > 0
                        ? { background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }
                        : undefined}>
                        {t.remainder > 0
                          ? `${payCurrency} ${money(t.remainder)} unallocated — ${s.payAs === PAY_AS.CUSTOMER_REFUND ? 'customer refund' : 'supplier advance'}`
                          : 'Fully allocated'}
                      </span>
                      {t.withheldCount > 0 && (
                        <span className="badge" style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}
                          title="Each withheld amount stays open on this supplier's ledger, with its reason, until you pay it or set a debit note off against it.">
                          {functionalCurrency} {money(t.withheldBase)} withheld on {t.withheldCount} bill{t.withheldCount > 1 ? 's' : ''} — kept open on their ledger
                        </span>
                      )}
                    </span>
                    <span>Allocated <strong>{payCurrency} {money(t.allocated)}</strong> of <strong>{payCurrency} {money(t.total)}</strong></span>
                  </div>
                  {(() => {
                    const supFx = r2(s.bills.reduce((x, b) => x + billFxDiff(b), 0));
                    if (Math.abs(supFx) < 0.005) return null;
                    return (
                      <div style={{ marginTop: '0.35rem', fontSize: '0.78rem',
                        color: supFx > 0 ? 'var(--danger-text, #b91c1c)' : 'var(--success-text, #15803d)' }}>
                        Exchange {supFx > 0 ? 'loss' : 'gain'} <strong>{money(Math.abs(supFx))} {functionalCurrency}</strong> on
                        these settlements — posts {supFx > 0 ? 'Dr' : 'Cr'} FX Gain / Loss (bills clear at their booked rate;
                        the payment leaves at {money(num(form.exchangeRate) || 1)}).
                      </div>
                    );
                  })()}
                  {sErr && <div className="form-error-message">{sErr}</div>}
                </div>
              </div>
            );
          })}

          {/* ---- Recoverable expenses (optional capability) ---- */}
          {recRows.length > 0 && (
            <div style={sectBox}>
              <div style={sectHead}>
                <strong style={{ fontSize: '0.85rem' }}>Recoverable from customers</strong>
                <span style={{ flex: '1 1 auto' }} />
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                  onClick={() => setRecRows([])}>Remove</button>
              </div>
              <div style={{ padding: '0.7rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {recRows.map(r => (
                  <div key={r.key} data-field={`rec-${r.key}`}>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ flex: '1 1 170px', minWidth: 150 }}>
                        <SyutSelect isAsync defaultOptions cacheOptions={false} loadOptions={loadCustomers}
                          menuPortalTarget={document.body}
                          seedOptions={r.partyId ? [{ value: r.partyId, label: r.partyLabel }] : undefined}
                          value={r.partyId}
                          onChange={v => { const id = (v as string) || ''; clearErr(`rec-${r.key}`);
                            updateRec(r.key, { partyId: id, partyLabel: partyLabelCache.current.get(id) || '' }); }}
                          placeholder="Customer" isClearable size="sm" />
                      </div>
                      <div style={{ flex: '1 1 170px', minWidth: 150 }}>
                        <SyutSelect isAsync defaultOptions cacheOptions={false} loadOptions={loadReimbItems}
                          menuPortalTarget={document.body}
                          seedOptions={r.serviceId ? [{ value: r.serviceId, label: r.serviceLabel }] : undefined}
                          value={r.serviceId}
                          onChange={v => { const id = (v as string) || ''; clearErr(`rec-${r.key}`);
                            updateRec(r.key, { serviceId: id, serviceLabel: serviceLabelCache.current.get(id) || '' }); }}
                          placeholder="Reimbursement item" isClearable size="sm" />
                      </div>
                      <div style={{ width: 180 }}>
                        <SyutSelect options={chargeToOptions} value={r.chargeTo}
                          onChange={v => updateRec(r.key, { chargeTo: (v as string) || '' })}
                          menuPortalTarget={document.body} isClearable={false} size="sm" />
                      </div>
                      <input type="number" min={0} step="0.01" className="form-input"
                        style={{ ...cellInput, width: 120 }} value={r.amount} placeholder="0.00"
                        onChange={e => { clearErr(`rec-${r.key}`); updateRec(r.key, { amount: e.target.value }); }} />
                      <button title="Remove" onClick={() => setRecRows(p => p.filter(x => x.key !== r.key))}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        <IconSystem.trash size={15} />
                      </button>
                    </div>
                    {err(`rec-${r.key}`) && <div className="form-error-message">{err(`rec-${r.key}`)}</div>}
                  </div>
                ))}
                <div>
                  <button className="btn btn-secondary" onClick={addRecRow}
                    style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                    <IconSystem.add size={13} /> Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---- Accounts ---- */}
          {glRows.length > 0 && (
            <div style={sectBox}>
              <div style={sectHead}>
                <strong style={{ fontSize: '0.85rem' }}>Accounts</strong>
                <span className="badge">Any ledger · direction per line</span>
                <span style={{ flex: '1 1 auto' }} />
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                  onClick={() => setGlRows([])}>Remove</button>
              </div>
              <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                    <th style={{ width: 30, padding: '0.4rem' }}>#</th>
                    <th style={{ width: 180, textAlign: 'left', padding: '0.4rem' }}>Charge to</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem' }}>Account</th>
                    <th style={{ width: 120, textAlign: 'right', padding: '0.4rem' }}>Amount</th>
                    <th style={{ width: 140, textAlign: 'center', padding: '0.4rem' }}>Effect on payment</th>
                    <th style={{ width: 34, padding: '0.4rem' }} />
                  </tr>
                </thead>
                <tbody>
                  {glRows.map((g, i) => {
                    const reduces = g.isCredit === true;
                    return (
                      <React.Fragment key={g.key}>
                        <tr data-field={`gl-${g.key}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{i + 1}</td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            {/* Charge-to first: it scopes which accounts the next cell offers. */}
                            <SyutSelect options={chargeToOptions} value={g.chargeTo}
                              onChange={v => updateGl(g.key, {
                                chargeTo: (v as string) || '', ledgerId: '', ledgerLabel: '',
                              })}
                              menuPortalTarget={document.body} isClearable={false} size="sm" />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', minWidth: 220 }}>
                            <SyutSelect isAsync defaultOptions cacheOptions={false}
                              loadOptions={(inp: string) => loadLedgers(inp, g.chargeTo)}
                              menuPortalTarget={document.body}
                              seedOptions={g.ledgerId ? [{ value: g.ledgerId, label: g.ledgerLabel }] : undefined}
                              value={g.ledgerId}
                              onChange={v => {
                                const id = (v as string) || '';
                                clearErr(`gl-${g.key}`);
                                updateGl(g.key, { ledgerId: id, ledgerLabel: ledgerLabelCache.current.get(id) || '' });
                              }}
                              placeholder="Account to post to" isClearable size="sm" />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                              value={g.amount} placeholder="0.00"
                              onChange={e => { clearErr(`gl-${g.key}`); updateGl(g.key, { amount: e.target.value }); }} />
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.25rem 0.4rem' }}>
                            <button onClick={() => updateGl(g.key, { isCredit: !reduces })}
                              title={g.isCredit === null
                                ? 'Direction taken from the account — click to set it explicitly'
                                : reduces ? 'Reduces the cash paid — click to switch' : 'Increases the cash paid — click to switch'}
                              style={{
                                border: '1px solid var(--form-border-color)', borderRadius: '0.35rem',
                                background: 'transparent', cursor: 'pointer', padding: '0.22rem 0.45rem',
                                fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                                color: reduces ? '#16a34a' : '#dc2626',
                                opacity: g.isCredit === null ? 0.7 : 1,
                              }}>
                              {reduces ? '− reduces (Cr)' : '+ adds (Dr)'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button title="Remove line" onClick={() => setGlRows(p => p.filter(x => x.key !== g.key))}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                              <IconSystem.trash size={15} />
                            </button>
                          </td>
                        </tr>
                        {err(`gl-${g.key}`) && (
                          <tr><td /><td colSpan={5} style={{ paddingBottom: '0.4rem' }}>
                            <div className="form-error-message">{err(`gl-${g.key}`)}</div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '0.5rem 0.7rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', background: 'var(--bg-secondary)' }}>
                <button className="btn btn-secondary" onClick={() => addGlRow()}
                  style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                  <IconSystem.add size={13} /> Line
                </button>
                {quickAdds.length > 0 && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '0.3rem' }}>Quick add</span>
                )}
                {quickAdds.map(q => (
                  <button key={q.id} className="btn btn-secondary" title={q.label}
                    onClick={() => addGlRow(q.id, q.label)}
                    style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                    {q.label.split(' - ').slice(1).join(' - ') || q.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ---- Add bar ---- */}
          {anySection && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={addSupplier}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                <IconSystem.add size={14} /> Supplier
              </button>
              {glRows.length === 0 && (
                <button className="btn btn-secondary" onClick={() => addGlRow()}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                  <IconSystem.add size={14} /> Accounts
                </button>
              )}
              {recoverableEnabled && recRows.length === 0 && (
                <button className="btn btn-secondary" onClick={addRecRow}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                  <IconSystem.add size={14} /> Recoverable expense
                </button>
              )}
            </div>
          )}

          {err('sections') && anySection && (
            <div className="form-error-message" data-field="sections">{err('sections')}</div>
          )}

          {/* ---- Net payable ---- */}
          <div style={{
            position: 'sticky', bottom: 0, marginTop: '1rem', padding: '0.65rem 0.9rem',
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
              <span>Adds to payment <strong>{money(totals.gross)}</strong></span>
              <span style={{ color: totals.reduces ? '#16a34a' : 'var(--text-muted)' }}>
                Reduces payment <strong>{money(totals.reduces)}</strong>
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                Net Payable&nbsp;
                <span style={{ color: totals.net > 0 ? 'var(--text-primary)' : '#dc2626' }}>
                  {payCurrency} {money(totals.net)}
                </span>
              </div>
              {isForeignPayment && num(form.exchangeRate) > 0 && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  ≈ {functionalCurrency} {money(r2(totals.net * num(form.exchangeRate)))} at {money(num(form.exchangeRate))}
                </div>
              )}
              {(() => {
                const totalFx = r2(suppliers.reduce((x, s) =>
                  x + s.bills.reduce((y, b) => y + billFxDiff(b), 0), 0));
                if (Math.abs(totalFx) < 0.005) return null;
                return (
                  <div style={{ fontSize: '0.72rem',
                    color: totalFx > 0 ? 'var(--danger-text, #b91c1c)' : 'var(--success-text, #15803d)' }}>
                    incl. exchange {totalFx > 0 ? 'loss' : 'gain'} {money(Math.abs(totalFx))} {functionalCurrency} ({totalFx > 0 ? 'Dr' : 'Cr'} FX Gain / Loss)
                  </div>
                );
              })()}
            </div>
          </div>

          {crossEntityTargets.length > 0 && (
            <div style={{
              fontSize: '0.72rem', color: 'var(--warning-text, #92400e)', background: 'var(--warning-bg, #fef3c7)',
              border: '1px solid #fde68a', borderRadius: '0.4rem', padding: '0.4rem 0.6rem', marginTop: '0.5rem',
            }}>
              Cross-company: {crossEntityTargets.join(', ')} will carry the cost and owe you the
              amount — the intercompany pair is posted automatically.
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={posting}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePostClick} disabled={posting || totals.net <= 0}>
            <IconSystem.check size={16} /> Post Payment
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen} title="Post Payment"
        message={
          isForeignPayment
            ? `Pay ${payCurrency} ${money(totals.net)} from ${form.paidFromLedgerLabel || 'the selected account'}? Bills clear at the rate they were booked at; the difference against ${form.exchangeRate} posts as an exchange gain or loss.`
            : `Pay ${payCurrency} ${money(totals.net)} from ${form.paidFromLedgerLabel || 'the selected account'}?`
        }
        confirmLabel="Post" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmPost} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

// ============================================================================
// VIEW MODAL — read-only posted payment.
// ============================================================================

const LINE_KIND_LABEL: Record<string, string> = {
  [PAYMENT_LINE_TYPES.BILL_SETTLEMENT]: 'Bill settled',
  [PAYMENT_LINE_TYPES.ON_ACCOUNT]: 'On account',
  [PAYMENT_LINE_TYPES.RECOVERABLE_EXPENSE]: 'Recoverable expense',
  [PAYMENT_LINE_TYPES.GL_LINE]: 'Account',
};

const PAY_AS_LABEL: Record<string, string> = {
  [PAY_AS.SUPPLIER_ADVANCE]: 'supplier advance',
  [PAY_AS.CUSTOMER_REFUND]: 'customer refund',
};

export interface PaymentVoucherViewModalProps {
  isOpen: boolean;
  payment: PaymentDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (paymentNumber: string) => void;
}

export const PaymentVoucherViewModal: React.FC<PaymentVoucherViewModalProps> = ({
  isOpen, payment, onClose, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<PaymentDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !payment?.id) { setDetail(null); return; }
    setLoading(true);
    paymentService.getById(payment.id)
      .then(setDetail)
      .catch(() => setDetail(payment))
      .finally(() => setLoading(false));
  }, [isOpen, payment]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !payment?.paymentNumber) return;
    minimize({
      id: `${tabId}-view-modal-${payment.paymentNumber}`,
      title: `View - ${payment.paymentNumber}`,
      moduleName: moduleName || 'Payment Voucher', tabId,
      onRestore: () => onRestoreView?.(payment.paymentNumber!),
    });
    onClose();
  }, [tabId, moduleName, payment, minimize, onClose, onRestoreView]);

  if (!isOpen || !payment) return null;
  const p = detail ?? payment;

  // Every total on this voucher is in the entity's own currency; `currencyCode` is what left the
  // bank. They are the same thing on a home-currency payment and must not be confused when they
  // are not — labelling a base total with the payment's currency would name the wrong money.
  const baseCcy = p.baseCurrencyCode || '';
  const viewIsForeign = !!p.currencyCode && !!baseCcy && p.currencyCode !== baseCcy;
  const totalRealizedFx = (p.lines || []).reduce((s, l) => s + (l.realizedFxAmount || 0), 0);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Payment — {p.paymentNumber || '(unnumbered)'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8125rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div><strong>{(p.paymentDate || '').slice(0, 10)}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Paid To</div><strong>{p.partyName || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Paid From</div><strong>{p.paidFromLedgerName || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div><span className="badge">{p.docStatus}</span></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Instrument</div>
              <strong>{p.instrumentType || '—'}{p.instrumentNumber ? ` · ${p.instrumentNumber}` : ''}</strong>
            </div>
            <div><div style={{ color: 'var(--text-muted)' }}>Bank</div><strong>{p.draweeBank || '—'}</strong></div>
            {/* Only worth a field when the money and the books are in different currencies. */}
            {viewIsForeign && (
              <div>
                <div style={{ color: 'var(--text-muted)' }}>Paid in</div>
                <strong>{p.currencyCode} @ {p.exchangeRate}</strong>
              </div>
            )}
            <div style={{ gridColumn: viewIsForeign ? 'span 1' : 'span 2' }}>
              <div style={{ color: 'var(--text-muted)' }}>Narration</div><strong>{p.narration || '—'}</strong>
            </div>
          </div>

          <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ width: 32, textAlign: 'center', padding: '0.5rem' }}>#</th>
                <th style={{ width: 160, textAlign: 'left', padding: '0.5rem' }}>Kind</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Details</th>
                <th style={{ width: 160, textAlign: 'left', padding: '0.5rem' }}>Charged To</th>
                <th style={{ width: 140, textAlign: 'right', padding: '0.5rem' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Loading…</td></tr>}
              {!loading && (p.lines || []).map((l, idx) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {LINE_KIND_LABEL[l.lineType] || l.lineType}
                    {l.payAs && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{PAY_AS_LABEL[l.payAs] || l.payAs}</div>
                    )}
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {[l.ledgerCode ? `${l.ledgerCode} - ${l.ledgerName}` : l.ledgerName,
                      l.settledReferenceNo, l.serviceName, l.partyName, l.narration]
                      .filter(Boolean).join(' · ') || '—'}
                    {!!l.withheldFcAmount && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--warning-text, #92400e)', marginTop: 2 }}>
                        Withheld {l.settledCurrency || ''} {money(l.withheldFcAmount)}
                        {l.withheldReasonLabel || l.withheldReasonCode ? ` · ${l.withheldReasonLabel || l.withheldReasonCode}` : ''}
                        {l.withheldRemarks ? ` · ${l.withheldRemarks}` : ''} — the bill closed; this amount stays open on the supplier's ledger
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {l.targetEntityCode ? `${l.targetEntityCode} — ${l.businessUnitName || ''}` : (l.businessUnitName || '—')}
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right' }}>
                    {isPaymentPartyLine(l.lineType) || !l.isCredit
                      ? money(l.amount) : `(${money(l.amount)})`}
                    {/* What was actually typed, when that is a different currency from the
                        amount above. Without it the voucher can only show its own conversion. */}
                    {l.fcAmount != null && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        {p.currencyCode} {money(l.fcAmount)}
                      </div>
                    )}
                    {/* How much of the BILL's own currency this settlement discharged — the
                        figure the exchange difference was worked from (ADR-0031 revision). */}
                    {l.settledFcAmount != null && l.settledCurrency
                      && !(l.settledCurrency === baseCcy && !l.fcAmount) && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        settled {l.settledCurrency} {money(l.settledFcAmount)}
                      </div>
                    )}
                    {/* The gain or loss this bill realised — §9's per-bill figure. */}
                    {!!l.realizedFxAmount && (
                      <div style={{ fontSize: '0.7rem', color: l.realizedFxAmount < 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)' }}>
                        {l.realizedFxAmount < 0 ? 'FX loss' : 'FX gain'} {money(Math.abs(l.realizedFxAmount))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <td colSpan={4} style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>Adds to payment</td>
                <td style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>{money(p.grossAmount)}</td>
              </tr>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <td colSpan={4} style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>Reduces payment</td>
                <td style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>{money(p.deductionTotal)}</td>
              </tr>
              {!!totalRealizedFx && (
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <td colSpan={4} style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>
                    Exchange {totalRealizedFx < 0 ? 'loss' : 'gain'} on settlement
                  </td>
                  <td style={{ textAlign: 'right', padding: '0.375rem 0.5rem',
                               color: totalRealizedFx < 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)' }}>
                    {money(Math.abs(totalRealizedFx))}
                  </td>
                </tr>
              )}
              <tr style={{ fontWeight: 700, background: 'var(--bg-secondary)' }}>
                {/* Base, not the payment currency — the totals above are the entity's own money. */}
                <td colSpan={4} style={{ textAlign: 'right', padding: '0.5rem' }}>Net Paid ({baseCcy})</td>
                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{money(p.netPaid)}</td>
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Posted{p.createdByName ? ` by ${p.createdByName}` : ''}{p.createdAt ? ` on ${new Date(p.createdAt).toLocaleString()}` : ''}.
            {p.icLink && ' An intercompany pair was posted with this payment.'}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default PaymentVoucherFormModal;
